
import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Api } from '../../../api/api';
import { importEnergyPrices, refreshEnergyPrices } from '../../../api/functions';
import { EnergyPriceImportResponse } from '../../../api/models/energy-price-import-response';
import { EnergyPriceRefreshResponse } from '../../../api/models/energy-price-refresh-response';
import { formatApiError } from '../../../common/utils/api-error';

type Phase = 'idle' | 'running' | 'success' | 'error';

/** Was die gewählte Datei zu enthalten scheint — nur zur Kontrolle vor dem Absenden. */
interface FilePreview {
  name: string;
  sizeLabel: string;
  rowCount: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
}

@Component({
  selector: 'app-price-admin',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './price-admin.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./price-admin.component.css'],
})
export class PriceAdminComponent {
  private readonly api = inject(Api);

  readonly importPath = importEnergyPrices.PATH;
  readonly refreshPath = refreshEnergyPrices.PATH;

  readonly csv = signal<string | null>(null);
  readonly preview = signal<FilePreview | null>(null);
  readonly replaceRange = signal(false);
  readonly isDragging = signal(false);

  readonly refreshStart = signal(isoDate(-1));
  readonly refreshEnd = signal(isoDate(1));

  readonly phase = signal<Phase>('idle');
  readonly runningLabel = signal('');
  readonly importResult = signal<EnergyPriceImportResponse | null>(null);
  readonly refreshResult = signal<EnergyPriceRefreshResponse | null>(null);
  readonly errorMessage = signal<string | null>(null);

  readonly isRunning = computed(() => this.phase() === 'running');
  readonly canImport = computed(() => !this.isRunning() && !!this.csv());
  readonly canRefresh = computed(() => !this.isRunning() && !!this.refreshStart() && !!this.refreshEnd());

  /** Die Tage, die der Importer wissen muss: unvollständig oder von einem anderen Tarif verdeckt. */
  readonly problemDays = computed(() =>
    (this.importResult()?.days ?? []).filter(
      (day) => !day.complete || (!!day.servedTariff && day.servedTariff !== day.tariff),
    ),
  );

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    await this.acceptFile(input.files?.[0]);
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isDragging.set(false);
    await this.acceptFile(event.dataTransfer?.files?.[0]);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(true);
  }

  onDragLeave(): void {
    this.isDragging.set(false);
  }

  clearFile(): void {
    this.csv.set(null);
    this.preview.set(null);
    this.dismiss();
  }

  async runImport(dryRun: boolean): Promise<void> {
    const csv = this.csv();
    if (!csv) {
      return;
    }

    this.start(dryRun ? 'Vorschau wird berechnet…' : 'Datei wird importiert…');
    try {
      const response = await this.api.invoke(importEnergyPrices, {
        body: { csv, replaceRange: this.replaceRange(), dryRun },
      });
      this.importResult.set(response ?? {});
      this.phase.set('success');
    } catch (error) {
      this.fail(error);
    }
  }

  async runRefresh(): Promise<void> {
    this.start('ENTSO-E wird abgefragt…');
    try {
      const response = await this.api.invoke(refreshEnergyPrices, {
        body: { startDate: this.refreshStart(), endDate: this.refreshEnd() },
      });
      this.refreshResult.set(response ?? {});
      this.phase.set('success');
    } catch (error) {
      this.fail(error);
    }
  }

  dismiss(): void {
    this.phase.set('idle');
    this.importResult.set(null);
    this.refreshResult.set(null);
    this.errorMessage.set(null);
  }

  formatRange(from?: string, to?: string): string {
    if (!from || !to) {
      return '–';
    }
    return `${formatTimestamp(from)} – ${formatTimestamp(to)}`;
  }

  private async acceptFile(file: File | undefined): Promise<void> {
    if (!file) {
      return;
    }
    this.dismiss();
    const content = await file.text();
    this.csv.set(content);
    this.preview.set(buildPreview(file, content));
  }

  private start(label: string): void {
    this.phase.set('running');
    this.runningLabel.set(label);
    this.importResult.set(null);
    this.refreshResult.set(null);
    this.errorMessage.set(null);
  }

  private fail(error: unknown): void {
    this.errorMessage.set(formatApiError(error));
    this.phase.set('error');
  }
}

/**
 * Nur so viel lokale Auswertung, dass man sieht, ob die richtige Datei gewählt wurde. Geprüft wird
 * die Datei ausschließlich im Backend — es kennt die Regeln, an denen sie später hängt.
 */
function buildPreview(file: File, content: string): FilePreview {
  const lines = content
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const dataLines = lines.slice(1);
  const timestamps = dataLines.map(firstField).filter((value): value is string => !!value);

  return {
    name: file.name,
    sizeLabel: formatSize(file.size),
    rowCount: dataLines.length,
    firstTimestamp: timestamps.length > 0 ? timestamps[0] : null,
    lastTimestamp: timestamps.length > 0 ? timestamps[timestamps.length - 1] : null,
  };
}

function firstField(line: string): string | null {
  const field = line.split(',')[0];
  if (field === undefined) {
    return null;
  }
  return field.replace(/^"|"$/g, '').trim() || null;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} kB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isoDate(dayOffset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
